import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, ButtonBase, IconButton, Stack, Typography } from '@mui/material';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import i18n from '../../i18n';
import type {
    MenuStoreLicenseCodeRow,
    MenuStoreMenuListItem,
    MenuStoreTemplateMenuRow,
    MenuStoreTemplateSecondBlock,
} from '../../models/menu-store.entity';
import { ReactComponent as BackIcon } from '../../static/images/svg/typcn_arrow-back.svg';
import { ReactComponent as MenuBookIcon } from '../../static/images/svg/material-symbols_menu-book-outline.svg';
import { ReactComponent as AutoStoriesIcon } from '../../static/images/svg/auto_stories.svg';
import { ReactComponent as BxsStoreIcon } from '../../static/images/svg/bxs_store.svg';
import SearchInput from '../../component/form/search-input.component';
import { ReactComponent as VisibilityIcon } from '../../static/images/svg/ic_round-visibility.svg';
import { ReactComponent as VisibilityOffIcon } from '../../static/images/svg/material-symbols_visibility-off-rounded.svg';
import { ReactComponent as DeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
import { ReactComponent as PasswordIcon } from '../../static/images/svg/mdi_password.svg';
import { ReactComponent as EditIcon } from '../../static/images/svg/fa7-regular_edit.svg';
import { ReactComponent as SaveIcon } from '../../static/images/svg/ic_round-save.svg';
import { ReactComponent as InfoIcon } from '../../static/images/svg/info.svg';
import defaultMenuImage from '../../static/images/default-menu.png';
import { MenuStoreActivateDialog } from './menu-store-activate-dialog.component';
import { MenuStoreUseNowDialog } from './menu-store-use-now-dialog.component';
import { MenuStoreLicenseManageDialog } from './menu-store-license-manage-dialog.component';
import { MenuStoreGenerateLicenseDialog } from './menu-store-generate-license-dialog.component';
import { MenuStoreContactDialog } from './menu-store-contact-dialog.component';
/** 详情布局、菜品栅格、激活/授权码/联系/浏览态等弹层样式（列表页不引入） */
import './scss/menu-store-detail-surface.scss';
import { CustomerLoading } from '../../component/loading/loading';
import {
    buildDefaultMenuStorePreviewMaskMap,
    flattenDishesFromStructure,
    formatMenuStoreDate,
    resolveMenuStoreUseNowActivationDateLabel,
    resolveMenuStoreUseNowRedemptionCodeLabel,
    formatPublisher,
    isMenuStoreDishMaskedInStore,
    menuStoreDishMaskKey,
    resolveMenuStoreBrowseDishTileImageUrl,
    resolveMenuStoreCoverImage,
    resolveMenuStoreDishImage,
} from './menu-store.helpers';

type UseNowStoreOption = {
    /** 门店 string_id。 */
    value: string;
    /** 门店展示名称。 */
    label: string;
};

export type MenuStoreDetailViewProps = {
    /** 当前菜单详情。 */
    item: MenuStoreMenuListItem | null;
    /** 详情来源，用于控制底部按钮组。 */
    detailSource: 'store-home' | 'my-activated' | 'my-uploaded';
    /** 返回列表回调。 */
    onBack: () => void;
    /** 是否隐藏详情页内返回按钮（由外层统一渲染头部时使用）。 */
    hideTopBack?: boolean;
    /** 从详情跳转到「我的菜单」回调。 */
    onOpenMyMenus?: () => void;
    /** 打开激活弹窗回调。 */
    onActivate: () => void;
    /** 激活弹窗开关。 */
    activateOpen: boolean;
    /** 激活码输入值。 */
    activateLicense: string;
    /** 激活码输入变化回调。 */
    onActivateLicenseChange: (value: string) => void;
    /** 关闭激活弹窗回调。 */
    onCloseActivate: () => void;
    /** 提交激活回调。 */
    onSubmitActivate: () => void;
    /** 激活提交状态。 */
    activateSubmitting: boolean;
    /** 立刻使用弹窗开关。 */
    useNowOpen: boolean;
    /** 立刻使用已选门店 string_id 列表（多选）。 */
    useNowStoreIds: string[];
    /** 打开立刻使用弹窗回调。 */
    onOpenUseNow: () => void;
    /** 关闭立刻使用弹窗回调。 */
    onCloseUseNow: () => void;
    /** 立刻使用：切换某门店选中状态。 */
    onToggleUseNowStore: (value: string) => void;
    /** 立刻使用提交回调。 */
    onSubmitUseNow: () => void;
    /** 立刻使用提交状态。 */
    useNowSubmitting: boolean;
    /** 可选门店列表。 */
    useNowStoreOptions: UseNowStoreOption[];
    /** 打开菜品详情回调。 */
    onOpenDishDetail: (dish: MenuStoreTemplateMenuRow) => void;
    /** 授权码管理：列表数据。 */
    licenseCodeRows: MenuStoreLicenseCodeRow[];
    /** 授权码管理：列表加载中。 */
    licenseCodesLoading: boolean;
    /** 授权码管理：按菜单 id 拉取列表。 */
    onRefreshLicenseCodesForMenu: (menuId: string) => void;
    /** 生成授权码：最近一次接口返回的码。 */
    generatedLicenseCodes: string[];
    /** 生成授权码：请求中。 */
    generateLicenseSubmitting: boolean;
    /** 生成授权码：提交生成（默认 1 条）。 */
    onRequestGenerateLicenses: (menuId: string) => void;
    /** 「我上传」创建者或「已激活」购买方在逻辑上可移除时由 store 置 true；已激活详情 UI 另受 `MENU_STORE_SHOW_REMOVE_ON_ACTIVATED_DETAIL` 控制。 */
    canRemoveMenu: boolean;
    /** 调用 `PATCH .../status` → `delisted`。 */
    onRemoveMenu: () => void;
    /** 移除请求中（禁用按钮）。 */
    removeMenuSubmitting: boolean;
    /** 编辑预览保存中。 */
    previewSaveSubmitting: boolean;
    /** 保存打码配置：一次 `PATCH .../dish-visibility`（`items` 批量）；成功返回 `true` 后组件退出编辑态。 */
    onSavePreviewEdit: (maskMap: Record<string, boolean>) => Promise<boolean>;
    /** `GET /v3/menu-store/menu/{id}` 拉取详情中，展示遮罩 */
    menuDetailLoading?: boolean;
    /**
     * 从**商店浏览列表**进入详情时为 true（与 `rootView === 'store-home'` 一致）。
     * 此时顶部与 Figma 商店首页一致：搜索 + 我的菜单 + 返回首页；与 `detailSource`（我上传/已激活/浏览）解耦。
     */
    showStoreBrowseToolbar?: boolean;
    /** 商店浏览详情顶栏搜索框初始文案（非受控，与列表已提交关键词对齐） */
    storeBrowseSearchInitial?: string;
    /** 商店浏览详情顶栏：提交搜索（回车 / 搜索图标 / 清空） */
    onStoreBrowseSearch?: (keyword: string) => void;
};

/**
 * 商家提议：「我的菜单 → 已激活」详情是否展示「移除菜单」入口。
 * 当前为 false（暂隐藏）；需恢复删除能力时改为 true，并核对 `.menu-store-detail__activated-actions` 双按钮布局。
 */
const MENU_STORE_SHOW_REMOVE_ON_ACTIVATED_DETAIL = false;

/**
 * 菜单商店详情视图（含详情主体、激活/立刻使用/授权码/编辑预览与移除菜单等）。
 */
export const MenuStoreDetailView: React.FC<MenuStoreDetailViewProps> = observer((props) => {
    /** 组件属性。 */
    const {
        item,
        detailSource,
        onBack,
        hideTopBack,
        onOpenMyMenus,
        onActivate,
        activateOpen,
        activateLicense,
        onActivateLicenseChange,
        onCloseActivate,
        onSubmitActivate,
        activateSubmitting,
        useNowOpen,
        useNowStoreIds,
        onOpenUseNow,
        onCloseUseNow,
        onToggleUseNowStore,
        onSubmitUseNow,
        useNowSubmitting,
        useNowStoreOptions,
        onOpenDishDetail,
        licenseCodeRows,
        licenseCodesLoading,
        onRefreshLicenseCodesForMenu,
        generatedLicenseCodes,
        generateLicenseSubmitting,
        onRequestGenerateLicenses,
        canRemoveMenu,
        onRemoveMenu,
        removeMenuSubmitting,
        previewSaveSubmitting,
        onSavePreviewEdit,
        menuDetailLoading = false,
    } = props;

    /** 当前选中的二级分类索引。 */
    const [sluIdx, setSluIdx] = useState(0);
    /** 授权码管理弹窗开关。 */
    const [licenseManageOpen, setLicenseManageOpen] = useState(false);
    /** 编辑预览弹窗开关。 */
    const [previewEditMode, setPreviewEditMode] = useState(false);
    /** 编辑预览模式下，菜品打码状态映射（true=打码，false=原图）。 */
    const [previewMaskMap, setPreviewMaskMap] = useState<Record<string, boolean>>({});
    /** 生成授权码弹窗开关。 */
    const [generateLicenseOpen, setGenerateLicenseOpen] = useState(false);
    /** 「联系我们」弹窗（按发布者拉取 contact-qr）。 */
    const [contactDialogOpen, setContactDialogOpen] = useState(false);

    /** 稳定引用父组件传入的拉取授权码列表回调 */
    const refreshLicenseRef = useRef(onRefreshLicenseCodesForMenu);
    refreshLicenseRef.current = onRefreshLicenseCodesForMenu;

    useEffect(() => {
        setSluIdx(0);
        setLicenseManageOpen(false);
        setPreviewEditMode(false);
        setPreviewMaskMap({});
        setGenerateLicenseOpen(false);
        setContactDialogOpen(false);
    }, [item?._id]);

    /**
     * 打开授权码管理弹窗时拉取列表（Apifox：`GET .../menu/{id}/license-codes`）。
     */
    useEffect(() => {
        /** `item` 在首帧可能仍为 null，需可选链避免 TS 与运行时异常 */
        const menuId = item?._id;
        if (!licenseManageOpen || !menuId) {
            return;
        }
        refreshLicenseRef.current(menuId);
    }, [licenseManageOpen, item?._id]);

    if (!item) {
        return null;
    }

    /** 发布人展示文本。 */
    const publisher = formatPublisher(item);
    /** 发布日期展示文本。 */
    const dateLabel = formatMenuStoreDate(item.created_at);
    /** 「立即使用」弹窗：激活日期（详情接口逐步对齐）。 */
    const useNowActivationLabel = resolveMenuStoreUseNowActivationDateLabel(item);
    /** 「立即使用」弹窗：兑换码（详情若返回 `license_code`）。 */
    const useNowRedemptionLabel = resolveMenuStoreUseNowRedemptionCodeLabel(item);
    /** 二级分类数组。 */
    const secondBlocks: MenuStoreTemplateSecondBlock[] = item.structure?.menuItems?.[0]?.secondData ?? [];
    /** 扁平菜品数组（无二级分类时兜底）。 */
    const allFlat = flattenDishesFromStructure(item);
    /** 当前分类下可见菜品。 */
    const visibleDishes: MenuStoreTemplateMenuRow[] = secondBlocks.length > 0 ? secondBlocks[sluIdx]?.menuData ?? [] : allFlat;
    /** 菜单图片地址（无图时回退默认菜单图）。 */
    const menuImageUrl = resolveMenuStoreCoverImage(item);
    /** 菜单图片背景变量样式（主图失败时回退默认图）。 */
    const menuImageStyle = {
        ['--menu-store-detail-menu-bg-image' as string]: menuImageUrl
            ? `url("${menuImageUrl}"), url("${defaultMenuImage}")`
            : `url("${defaultMenuImage}")`,
    } as React.CSSProperties;

    /**
     * 当前分类下菜品在编辑预览映射中的键（与全量 `buildDefaultMenuStorePreviewMaskMap` 一致）。
     */
    const maskKeyForVisibleDish = (dishRow: MenuStoreTemplateMenuRow, dishIdx: number): string =>
        menuStoreDishMaskKey(secondBlocks.length > 0 ? sluIdx : -1, dishIdx, dishRow);

    /**
     * 进入编辑预览模式时初始化打码状态（**整本菜单**默认全部打码）。
     */
    const enterPreviewEditMode = (): void => {
        setPreviewMaskMap(buildDefaultMenuStorePreviewMaskMap(item));
        setPreviewEditMode(true);
    };

    /**
     * 切换单个菜品的打码状态。
     */
    const toggleDishMask = (dishKey: string, masked: boolean): void => {
        setPreviewMaskMap((prev) => ({ ...prev, [dishKey]: masked }));
    };

    /**
     * 保存编辑预览：批量提交 `dish-visibility`；成功时退出编辑态。
     */
    const savePreviewEdit = async (): Promise<void> => {
        const ok = await onSavePreviewEdit(previewMaskMap);
        if (ok) {
            setPreviewEditMode(false);
        }
    };

    /**
     * 取消编辑预览并回到只读态，不保存本次改动。
     */
    const cancelPreviewEdit = (): void => {
        setPreviewMaskMap({});
        setPreviewEditMode(false);
    };

    return (
        <Stack spacing={2} className="menu-store-page__detail">
            {menuDetailLoading ? (
                <Box role="status" aria-live="polite" aria-label={i18n.t('general_text_loading')}>
                    <CustomerLoading />
                </Box>
            ) : null}
            {props.showStoreBrowseToolbar ? (
                <Box className="menu-store-page__toolbar">
                    <Box className="menu-store-page__toolbar-left">
                        <Box className="menu-store-page__search-pill">
                            <SearchInput
                                className="menu-store-page__search-field"
                                allowClear
                                placeholder={i18n.t('menu_store_search_placeholder')}
                                initialValue={props.storeBrowseSearchInitial ?? ''}
                                onSearch={(v) => props.onStoreBrowseSearch?.(v)}
                            />
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        className="menu-store-page__btn-my-menus"
                        startIcon={<BxsStoreIcon className="menu-store-page__btn-my-menus-icon" aria-hidden />}
                        onClick={() => onOpenMyMenus?.()}
                    >
                        {i18n.t('menu_store_tab_my_menus')}
                    </Button>
                    <Button className="menu-store-page__btn-back-home" startIcon={<BackIcon />} onClick={onBack}>
                        {i18n.t('menu_store_btn_back_home')}
                    </Button>
                </Box>
            ) : !hideTopBack ? (
                <Button className="menu-store-detail__back" startIcon={<BackIcon />} onClick={onBack} size="small">
                    {i18n.t('menu_store_btn_back')}
                </Button>
            ) : null}

            <Box className="menu-store-detail__layout">
                <Box className="menu-store-detail__side">
                    <Box className="menu-store-detail__hero">
                        <Box className="menu-store-detail__hero-photo" aria-hidden style={menuImageStyle} />
                        <Box className="menu-store-detail__hero-overlay" aria-hidden />
                        <MenuBookIcon className="menu-store-detail__hero-icon" aria-hidden />
                        <Typography variant="body1" component="p" className="menu-store-detail__hero-line">
                            {item.name}
                        </Typography>
                        <Typography variant="body1" component="p" className="menu-store-detail__hero-line">
                            {dateLabel}
                        </Typography>
                        <Typography variant="body1" component="p" className="menu-store-detail__hero-line" noWrap title={publisher}>
                            {publisher}
                        </Typography>
                    </Box>

                    {secondBlocks.length > 0 ? (
                        <Box className="menu-store-detail__slu-panel">
                            <Box className="menu-store-detail__slu-panel-head">
                                <AutoStoriesIcon className="menu-store-detail__slu-panel-icon" aria-hidden />
                                <Typography variant="h4" component="h2" className="menu-store-detail__slu-panel-title">
                                    {i18n.t('menu_store_detail_subcategories')}
                                </Typography>
                            </Box>
                            <Box className="menu-store-detail__slu-list" role="tablist" aria-label={i18n.t('menu_store_detail_subcategories')}>
                                {secondBlocks.map((sec, idx) => {
                                    /** 原始分类名称。 */
                                    const rawLabel = sec.dmi_slu_name?.trim() || '—';
                                    /** 带圆点前缀的分类名称。 */
                                    const labelText = rawLabel;
                                    // const labelText = rawLabel.startsWith('·') ? rawLabel : `·${rawLabel}`;
                                    /** 当前项是否为激活态。 */
                                    const isActive = idx === sluIdx;
                                    return (
                                        <ButtonBase
                                            key={`${sec.dmi_slu_name}-${idx}`}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            disableRipple
                                            className={['menu-store-detail__slu-item', isActive ? 'menu-store-detail__slu-item--active' : ''].filter(Boolean).join(' ')}
                                            onClick={() => setSluIdx(idx)}
                                        >
                                            <span className="menu-store-detail__slu-item-content">
                                                <span className="menu-store-detail__slu-item-text" title={labelText}>
                                                    {labelText}
                                                </span>
                                            </span>
                                        </ButtonBase>
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : null}

                    {item.description ? (
                        <Typography variant="body2" color="text.secondary" className="menu-store-detail__desc">
                            {item.description}
                        </Typography>
                    ) : null}
                </Box>

                <Box className="menu-store-detail__main">
                    <Box className="menu-store-detail__dish-panel">
                        <Box className="menu-store-detail__dish-grid">
                            {visibleDishes.length === 0 ? (
                                <Box className="menu-store-detail__dish-empty">
                                    <Typography variant="body2" color="text.secondary">
                                        {i18n.t('general_text_no_or_more_data')}
                                    </Typography>
                                </Box>
                            ) : (
                                visibleDishes.map((dishRow, idx) => {
                                    /** 是否为「我上传的菜单」详情卡片。 */
                                    const isUploadedDishTile = detailSource === 'my-uploaded';
                                    /** 当前菜品打码状态键。 */
                                    const dishMaskKey = maskKeyForVisibleDish(dishRow, idx);
                                    /** 编辑态读映射；只读态读 `is_detail_visible`。 */
                                    const isDishMasked = previewEditMode
                                        ? previewMaskMap[dishMaskKey] ?? true
                                        : isMenuStoreDishMaskedInStore(dishRow);
                                    /** 是否为商店首页进入的未激活浏览详情 */
                                    const isBrowseStoreHome = detailSource === 'store-home';
                                    /** 列表瓦片图：未激活不开放 → OSS 打码；未激活已开放 / 其余 → 原解析地址 */
                                    const dishImageUrl = isBrowseStoreHome
                                        ? resolveMenuStoreBrowseDishTileImageUrl(dishRow, detailSource)
                                        : resolveMenuStoreDishImage(dishRow);
                                    /** 菜品图片背景变量样式（主图失败时回退默认图）。 */
                                    const dishImageStyle = {
                                        ['--menu-store-detail-dish-bg-image' as string]: dishImageUrl
                                            ? `url("${dishImageUrl}"), url("${defaultMenuImage}")`
                                            : `url("${defaultMenuImage}")`,
                                    } as React.CSSProperties;
                                    /** 浏览态开放菜品：原图展示，样式上减弱整图压暗 */
                                    const browseTileOpenClass =
                                        isBrowseStoreHome && !isDishMasked ? 'menu-store-dish-tile--browse-open' : '';
                                    /** 浏览态不开放：打码图 + 保留压暗叠层 */
                                    const browseTileLockedClass =
                                        isBrowseStoreHome && isDishMasked ? 'menu-store-dish-tile--browse-locked' : '';
                                    /** 未激活列表第二名称（有则展示） */
                                    const browseName2 = isBrowseStoreHome ? (dishRow.item_name2 ?? '').trim() : '';
                                    /** 当前卡片是否允许打开菜品详情（商店浏览未激活亦可进入浏览态详情）。 */
                                    const canOpenDishDetail = !(isUploadedDishTile && previewEditMode);
                                    return (
                                        <Box className="menu-store-detail__dish-grid-item" key={`${dishRow.item_name1}-${idx}`}>
                                            <Box
                                                className={[
                                                    'menu-store-dish-tile',
                                                    isUploadedDishTile ? 'menu-store-dish-tile--uploaded' : '',
                                                    canOpenDishDetail ? 'menu-store-dish-tile--clickable' : '',
                                                    browseTileOpenClass,
                                                    browseTileLockedClass,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                                role={canOpenDishDetail ? 'button' : undefined}
                                                tabIndex={canOpenDishDetail ? 0 : -1}
                                                onClick={() => {
                                                    if (canOpenDishDetail) {
                                                        onOpenDishDetail(dishRow);
                                                    }
                                                }}
                                                onKeyDown={(event) => {
                                                    if (!canOpenDishDetail) {
                                                        return;
                                                    }
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        onOpenDishDetail(dishRow);
                                                    }
                                                }}
                                            >
                                                <Box
                                                    className={[
                                                        'menu-store-dish-tile__img',
                                                        browseTileOpenClass ? 'menu-store-dish-tile__img--browse-open' : '',
                                                        browseTileLockedClass ? 'menu-store-dish-tile__img--browse-locked' : '',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ')}
                                                    aria-hidden
                                                    style={dishImageStyle}
                                                />
                                                {isUploadedDishTile && (isDishMasked || previewEditMode) ? (
                                                    <Box className={['menu-store-dish-tile__mask', previewEditMode ? 'menu-store-dish-tile__mask--editable' : ''].filter(Boolean).join(' ')}>
                                                        {previewEditMode ? (
                                                            <IconButton
                                                                type="button"
                                                                className="menu-store-dish-tile__mask-btn"
                                                                onClick={() => toggleDishMask(dishMaskKey, !isDishMasked)}
                                                                aria-label={isDishMasked ? i18n.t('menu_store_preview_unmask_aria') : i18n.t('menu_store_preview_mask_aria')}
                                                                size="small"
                                                            >
                                                                {isDishMasked ? (
                                                                    <VisibilityOffIcon className="menu-store-dish-tile__mask-icon" aria-hidden />
                                                                ) : (
                                                                    <VisibilityIcon className="menu-store-dish-tile__mask-icon" aria-hidden />
                                                                )}
                                                            </IconButton>
                                                        ) : (
                                                            <VisibilityOffIcon className="menu-store-dish-tile__mask-icon" aria-hidden />
                                                        )}
                                                    </Box>
                                                ) : null}
                                                <Box className="menu-store-dish-tile__name-stack">
                                                    <Typography
                                                        variant="body1"
                                                        className={['menu-store-dish-tile__name', isUploadedDishTile ? 'menu-store-dish-tile__name--uploaded' : 'menu-store-dish-tile__name--center'].join(' ')}
                                                        noWrap={isUploadedDishTile}
                                                        title={dishRow.item_name1}
                                                    >
                                                        {dishRow.item_name1 || '—'}
                                                    </Typography>
                                                    {browseName2 ? (
                                                        <Typography
                                                            variant="body2"
                                                            component="p"
                                                            className={['menu-store-dish-tile__name menu-store-dish-tile__name--browse-sub', browseTileOpenClass ? 'menu-store-dish-tile__name--browse-sub-open' : ''].filter(Boolean).join(' ')}
                                                            title={browseName2}
                                                        >
                                                            {browseName2}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>

                    <Box className="menu-store-detail__actions">
                        {detailSource === 'store-home' && item.status === 'published' ? (
                            <Button className="menu-store-detail__btn-activate-soft" variant="contained" disableElevation onClick={onActivate}>
                                {i18n.t('menu_store_btn_activate')}
                            </Button>
                        ) : null}
                        {detailSource === 'my-activated' ? (
                            /* 已激活详情：双按钮时移除在左、立即使用在右；仅「立刻使用」时加 `--single` 右对齐 */
                            <Box
                                className={[
                                    'menu-store-detail__activated-actions',
                                    !MENU_STORE_SHOW_REMOVE_ON_ACTIVATED_DETAIL ? 'menu-store-detail__activated-actions--single' : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                            >
                                {MENU_STORE_SHOW_REMOVE_ON_ACTIVATED_DETAIL ? (
                                    <LoadingOverlayButton
                                        className="menu-store-detail__btn-uploaded-action menu-store-detail__btn-uploaded-action--danger"
                                        variant="outlined"
                                        loading={removeMenuSubmitting}
                                        loadingIndicatorSize={22}
                                        disabled={!canRemoveMenu}
                                        startIcon={<DeleteForeverIcon className="menu-store-detail__uploaded-action-icon" aria-hidden />}
                                        onClick={() => onRemoveMenu()}
                                    >
                                        {i18n.t('menu_store_btn_remove_menu')}
                                    </LoadingOverlayButton>
                                ) : null}
                                <Button className="menu-store-detail__btn-activate-soft" variant="contained" disableElevation onClick={onOpenUseNow}>
                                    {i18n.t('menu_store_btn_use_now')}
                                </Button>
                            </Box>
                        ) : null}
                        {detailSource === 'store-home' ? (
                            <Button className="menu-store-detail__btn-contact-solid" variant="contained" disableElevation onClick={() => setContactDialogOpen(true)}>
                                {i18n.t('menu_store_btn_contact')}
                            </Button>
                        ) : null}
                        {detailSource === 'my-uploaded' ? (
                            <Box className="menu-store-detail__uploaded-actions">
                                {previewEditMode ? (
                                    <>
                                        <Box className="menu-store-detail__preview-tip">
                                            <InfoIcon className="menu-store-detail__preview-tip-icon" aria-hidden />
                                            <Typography variant="body2" className="menu-store-detail__preview-tip-text">
                                                {i18n.t('menu_store_preview_tip_text')}
                                            </Typography>
                                        </Box>
                                        <Box className="menu-store-detail__preview-actions-right">
                                            <LoadingOverlayButton
                                                className="menu-store-detail__btn-uploaded-primary"
                                                variant="contained"
                                                disableElevation
                                                loading={previewSaveSubmitting}
                                                loadingIndicatorSize={22}
                                                startIcon={<SaveIcon className="menu-store-detail__uploaded-action-icon" />}
                                                onClick={() => void savePreviewEdit()}
                                            >
                                                {i18n.t('menu_store_btn_save_edit')}
                                            </LoadingOverlayButton>
                                            <Button className="menu-store-detail__btn-uploaded-action menu-store-detail__btn-uploaded-action--primary" variant="outlined" onClick={cancelPreviewEdit}>
                                                {i18n.t('menu_store_btn_cancel_edit')}
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Box className="menu-store-detail__uploaded-actions-left">
                                            <LoadingOverlayButton
                                                className="menu-store-detail__btn-uploaded-action menu-store-detail__btn-uploaded-action--danger"
                                                variant="outlined"
                                                loading={removeMenuSubmitting}
                                                loadingIndicatorSize={22}
                                                disabled={!canRemoveMenu}
                                                startIcon={<DeleteForeverIcon className="menu-store-detail__uploaded-action-icon" aria-hidden />}
                                                onClick={() => onRemoveMenu()}
                                            >
                                                {i18n.t('menu_store_btn_remove_menu')}
                                            </LoadingOverlayButton>
                                            <Button
                                                className="menu-store-detail__btn-uploaded-action menu-store-detail__btn-uploaded-action--primary"
                                                variant="outlined"
                                                startIcon={<PasswordIcon className="menu-store-detail__uploaded-action-icon" />}
                                                onClick={() => setLicenseManageOpen(true)}
                                            >
                                                {i18n.t('menu_store_btn_license_manage')}
                                            </Button>
                                            <Button
                                                className="menu-store-detail__btn-uploaded-action menu-store-detail__btn-uploaded-action--primary"
                                                variant="outlined"
                                                startIcon={<EditIcon className="menu-store-detail__uploaded-action-icon" />}
                                                onClick={enterPreviewEditMode}
                                            >
                                                {i18n.t('menu_store_btn_preview_edit')}
                                            </Button>
                                        </Box>
                                        <Button className="menu-store-detail__btn-uploaded-primary" variant="contained" disableElevation onClick={() => setGenerateLicenseOpen(true)}>
                                            {i18n.t('menu_store_btn_generate_license')}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Box>

            <MenuStoreActivateDialog
                open={activateOpen}
                menuName={item.name}
                menuImageStyle={menuImageStyle}
                activateLicense={activateLicense}
                onActivateLicenseChange={onActivateLicenseChange}
                onClose={onCloseActivate}
                onSubmit={onSubmitActivate}
                submitting={activateSubmitting}
            />

            <MenuStoreUseNowDialog
                open={useNowOpen}
                menuName={item.name}
                dateLabel={dateLabel}
                publisher={publisher}
                activationDateLabel={useNowActivationLabel}
                redemptionCodeLabel={useNowRedemptionLabel}
                menuImageStyle={menuImageStyle}
                useNowStoreIds={useNowStoreIds}
                useNowStoreOptions={useNowStoreOptions}
                onClose={onCloseUseNow}
                onToggleUseNowStore={onToggleUseNowStore}
                onSubmit={onSubmitUseNow}
                submitting={useNowSubmitting}
            />

            <MenuStoreLicenseManageDialog
                open={licenseManageOpen}
                menuName={item.name}
                dateLabel={dateLabel}
                menuImageStyle={menuImageStyle}
                loading={licenseCodesLoading}
                licenseRows={licenseCodeRows}
                onClose={() => setLicenseManageOpen(false)}
                generateLicenseSubmitting={generateLicenseSubmitting}
                onGenerateLicense={() => onRequestGenerateLicenses(item._id)}
            />

            <MenuStoreGenerateLicenseDialog
                open={generateLicenseOpen}
                menuId={item._id}
                loading={generateLicenseSubmitting}
                generatedCodes={generatedLicenseCodes}
                onGenerate={onRequestGenerateLicenses}
                onClose={() => setGenerateLicenseOpen(false)}
            />

            <MenuStoreContactDialog
                open={contactDialogOpen}
                onClose={() => setContactDialogOpen(false)}
                creatorStaffId={item.created_by}
                fallbackProducerName={publisher}
            />
        </Stack>
    );
});
